import { AxiosError } from 'axios';
import { authHeader, authUploadService } from 'services';
import { IEmployee, IEmployeeCreate, IEmployeeList } from 'types/Employee';
import { IManagementList } from 'types/Management';
import { getUserToken } from 'util/auth';

// import { getUserToken } from 'util/auth';
import { Api } from './api/axios.service';

const FILE_DOWNLOAD_URL = `${process.env.REACT_APP_BASE_API_URL}/api/company`;

/**
 * List employees
 *
 * @param {number} page
 * @return {*}  {Promise<IEmployeeList>}
 */
const getEmployeeList = async (
  page: number,
  amount: number,
  filter: string
): Promise<IEmployeeList> => {
  try {
    const { data } = await Api.get<IEmployeeList>(
      `/company/findAllCompanyTutors?page=${page}&amount=${amount}&filter=${filter}`,
      {
        headers: authHeader(),
      }
    );
    return data;
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível listar os colaboradores'
    );
  }
};

const getResponsibleRHList = async (id: string): Promise<IManagementList> => {
  try {
    const { data } = await Api.get<IManagementList>(
      `/company/findAllUsers/${id}`,
      {
        headers: authHeader(),
      }
    );
    return data;
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível listar os colaboradores'
    );
  }
};

/**
 * Create employee through form
 *
 * @param {IEmployeeCreate} employee
 * @return {*}  {Promise<void>}
 */
const createEmployee = async (employee: IEmployeeCreate): Promise<void> => {
  try {
    const response = await Api.post('/company/createTutor', employee, {
      headers: authHeader(),
    })

    return response.data;
  } catch (error) {
    const { response } = error as AxiosError;
    throw new Error(
      response?.data?.error || 'Não foi possível atualizar o colaborador'
    );
  }
};

/**
 * Update an employee from Id
 *
 * @param {(Pick<IEmployee, 'id' | 'phone' | 'email'>)} employee
 * @return {*}  {Promise<void>}
 */
const updateEmployee = async ({
  email,
  phone,
  id,
}: Pick<IEmployee, 'id' |'email' | 'phone'>): Promise<void> => {
  try {
    return Api.put(
      `/company/tutor/updateFromCompany/${id}`,
      { phone, email },
      {
        headers: authHeader(),
      }
    ).then((response) => response.data);
  } catch (error) {
    const { response } = error as AxiosError;
    throw new Error(
      response?.data?.error || 'Não foi possível atualizar o colaborador'
    );
  }
};

/**
 * Create employees through worksheet
 *
 * @param {File} groupEmployees
 * @return {*}  {Promise<void>}
 */
const createGroupEmployeeWorksheet = async (
  groupEmployees: File
): Promise<void> => {
  const formData = new FormData();

  if (!groupEmployees) {
    return;
  }

  formData.append('tutorSignUpSpreadsheet', groupEmployees);

  try {
    await Api.post('/company/createTutorBySpreadsheet', formData, {
      headers: authUploadService(),
    });
  } catch (reason) {
    const { response } = reason as AxiosError<{
      error: Array<{ message: string; line: number }>;
    }>;

    const error = response?.data?.error;

    if (Array.isArray(error)) {
      return Promise.reject(error);
    }

    throw new Error(
      typeof error === 'string'
        ? error
        : 'Não foi possível cadastrar os colaboradores'
    );
  }
};

/**
 * Download worksheet model
 *
 * @return {*}
 */
const getEmployeesWorksheet = (): string => {
  return `${FILE_DOWNLOAD_URL}/downloadTutorSignUpSpreadsheet/?access_token=Bearer ${
    getUserToken().token
  }`;
};

/* Delete employee by id
 *
 * @param {string[]} employeeIds
 * @return {*}
 */
const deleteEmployeeById = async (employeeIds: string[]): Promise<any> => {
  try {
    await Api.put(
      `/company/tutor/removeCompanyBound`,
      {
        tutorIds: employeeIds,
      },
      {
        headers: authHeader(),
      }
    );
    return true;
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível deletar o colaborador'
    );
  }
};

export const EmployeeService = {
  getEmployeeList,
  getResponsibleRHList,
  createEmployee,
  updateEmployee,
  getEmployeesWorksheet,
  createGroupEmployeeWorksheet,
  deleteEmployeeById,
};
